import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ListingContext from './Listing.context';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { useNavigate, useParams } from 'react-router-dom';
import {
  WorkflowConfiguration,
  WorkflowConfigurationStepsCapacity,
} from '../Listing/Listing.context';
import { DropzoneFile } from 'components/Dropzone/utils';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { LeadsStateChangedEventData } from 'services/socketService/socketEventData';
import FourDotLoader from 'components/FourDotLoader';
import api from 'api';
import { Listing, ListingEnquiry } from 'models/Listing';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DataRoomDashboardStats, DealRoom } from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import usePremiumFeatures from 'hooks/usePremiumFeatures';
import { BUSINESS_ROUTE } from 'data/global';
import { getIsListingOwner } from 'utils';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';
import useTransferDealToEnterpriseColleague from 'router/subrouters/Dash/subrouters/hooks/useTransferDealToEnterpriseColleague';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { useTranslation } from 'react-i18next';
import { Lead } from 'models/Lead';
import useRefetch from 'hooks/useRefetch';

export const isWorkflowConfiguration = (
  property: WorkflowConfiguration | undefined,
): property is WorkflowConfiguration =>
  !!(property as WorkflowConfiguration)?.workflowConfigurationSteps;

const ListingProvider: React.FC = ({ children }) => {
  const [dealRoom, setDealRoom] = useState<DealRoom>();
  const [listing, setListing] = useState<Partial<Listing>>();
  const [enquiry, setEnquiry] = useState<Partial<ListingEnquiry>>();
  const [dashboardStats, setDashboardStats] =
    useState<DataRoomDashboardStats>();
  const [statsIsFetching, setStatsIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [workflowConfiguration, setWorkflowConfiguration] =
    useState<WorkflowConfiguration>();
  const [leads, setLeads] = useState<Partial<Lead>[]>([]);

  const { refetchFlag: refetchLeadsFlag, refetch: refetchLeads } = useRefetch();

  const {
    isSuperAdmin,
    currentUser: { _id: currentUserId, isGridViewForLeads },
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { id: dealRoomId } = useParams();

  const { t } = useTranslation();

  const {
    refetchFlag,
    transferredTo,
    isUndoTransferToEnterpriseColleagueAvailable,
    onUndoTransferToEnterpriseColleague,
    onTransferToEnterpriseColleague,
  } = useTransferDealToEnterpriseColleague(dealRoomId);

  const { hasUnlockPremiumFeatures } = usePremiumFeatures(dealRoom);

  const isTableView = useMemo(() => !isGridViewForLeads, [isGridViewForLeads]);

  const listingId = useMemo(() => listing?._id, [listing?._id]);

  const isListingOwner = useMemo(
    () => getIsListingOwner(currentUserId, listing?.userId, listing?.isCoOwner),
    [currentUserId, listing?.isCoOwner, listing?.userId],
  );

  const canManageDeal = useMemo(
    () => isSuperAdmin || isListingOwner,
    [isListingOwner, isSuperAdmin],
  );

  const canAccessDataRoom = useCanAccessDataRoom()(
    dealRoom,
    dealRoom?.hasDataRoomAccess,
  );

  const workflowConfigurationStepsCapacity = useMemo(
    () =>
      isWorkflowConfiguration(workflowConfiguration)
        ? workflowConfiguration.workflowConfigurationSteps.reduce(
            (acc, { step, maxCapacity }) => {
              acc[step] = maxCapacity;
              return acc;
            },
            {} as WorkflowConfigurationStepsCapacity,
          )
        : null,
    [workflowConfiguration],
  );

  const navigate = useNavigate();

  const uploadListingFile: DropzoneUpload = useCallback(
    async (file, fileName, config, customName) => {
      setIsUploading(true);
      try {
        const { uploadedFile, listing } = await api.listings.uploadFile(
          file,
          fileName,
          listingId,
          config,
          customName,
        );

        setListing(listing);
        return uploadedFile;
      } catch (e) {
        popServerError(e);
      } finally {
        setIsUploading(false);
      }
    },
    [listingId, popServerError],
  );

  const deleteListingFile: DropzoneDelete = useCallback(
    async (file, fileName) => {
      try {
        const {
          data: { fileId, listing: updatedListing },
        } = await api.listings.deleteFile(listingId, fileName, file.id);

        setListing(updatedListing);
        return fileId;
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, popServerError],
  );

  const updateGallery = useCallback(
    (images: DropzoneFile[]) =>
      setListing((prev) => ({ ...prev, gallery: images })),
    [],
  );

  const updateEnquiryHandle = useCallback(
    ({ updatedListingEnquiry }: LeadsStateChangedEventData) => {
      const { listing, workflowConfiguration, ...rest } = updatedListingEnquiry;

      if (listingId !== listing._id) return;

      setListing(listing);
      setWorkflowConfiguration(workflowConfiguration);
      setEnquiry(rest);
      refetchLeads();
    },
    [listingId, refetchLeads],
  );

  useSocketEvent(LISTEN_EVENTS.LEADS_STATE_CHANGED, updateEnquiryHandle);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await api.dealRoom.getRoom(dealRoomId);

        const {
          listingEnquiry: { listing, workflowConfiguration, ...rest },
        } = data;

        setWorkflowConfiguration(workflowConfiguration);
        setEnquiry(rest);
        setListing(listing);
        setDealRoom(data);
      } catch (e) {
        popServerError(e);
        navigate(BUSINESS_ROUTE, { replace: true });
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealRoomId, refetchFlag, popServerError]);

  useEffect(() => {
    if (!listing?.dataRoomId || !hasUnlockPremiumFeatures) return;

    (async () => {
      try {
        setStatsIsFetching(true);
        const { data } = await api.dataRoom.stats.getDashStats(
          listing.dataRoomId,
        );
        setDashboardStats(data);
      } catch (e) {
        return;
      } finally {
        setStatsIsFetching(false);
      }
    })();
  }, [listing?.dataRoomId, hasUnlockPremiumFeatures]);

  useEffect(() => {
    if (!dealRoom?.listingEnquiryId) return;

    (async () => {
      try {
        const { data } = await api.portfolio.seller.getLeads(
          dealRoom?.listingEnquiryId,
        );
        setLeads(data);
      } catch (e) {
        return;
      }
    })();
  }, [dealRoom?.listingEnquiryId, refetchLeadsFlag]);

  return (
    <ListingContext.Provider
      value={{
        listing,
        dealRoom,
        enquiry,
        workflowConfigurationStepsCapacity,
        isTableView,
        isUploading,
        workflowConfiguration,
        canManageDeal,
        hasUnlockPremiumFeatures,
        dashboardStats,
        statsIsFetching,
        canAccessDataRoom,
        gallery: listing?.gallery,
        areActionsDisabled: !canManageDeal,
        isListingOwner,
        isTransferringDeal: isUndoTransferToEnterpriseColleagueAvailable,
        leads,
        refetchLeads,
        onTransferToEnterpriseColleague,
        uploadListingFile,
        deleteListingFile,
        setListing,
        setEnquiry,
        setDealRoom,
        updateGallery,
        setIsUploading,
      }}
    >
      {isLoading ? <FourDotLoader /> : children}

      <PopNotificationWithUndo
        isUndoAvailable={isUndoTransferToEnterpriseColleagueAvailable}
        onUndo={onUndoTransferToEnterpriseColleague}
      >
        <p>
          {t('dealIsTransferringTo', {
            transferredTo,
          })}
        </p>
      </PopNotificationWithUndo>
    </ListingContext.Provider>
  );
};

export default ListingProvider;
